import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '../../../components/common/Modal';
import ConfirmModal from '../../../components/common/ConfirmModal';
import Loading from '../../../components/common/Loading';
import LoadingButton from '../../../components/common/LoadingButton';
import LoaderSpinner from '../../../components/common/LoaderSpinner';
import UnlimitedAdminList from '../../../components/bridge/UnlimitedAdminList';
import useNotify from '../../../hooks/useNotify';
import accessRequestService from '../../../services/accessRequestService';
import { getRespondedAdminName } from '../../../components/notifications/NotificationBell';
import CompanyAdminBridgePanel from '../components/CompanyAdminBridgePanel';
import '../../../components/notifications/NotificationBell.css';
import './CompaniesPage.css';
import {
  emptyPerson,
  getDialCodeForBridgeCountry,
  applyCompanyCountryToAdmins,
  mapPersonFromApi,
} from '../../../utils/bridgeStaff';
import {
  filterCompanyBridgeAdmins,
  validateCompanyBridgeAdmins,
} from '../../../utils/companyBridgeAdmins';
import {
  fetchCompanies,
  fetchCompanyById,
  createCompany,
  updateCompany,
  deleteCompany,
  clearCompanyDetail,
  selectCompanies,
  selectCompaniesLoading,
  selectCompanyDetail,
  selectCompanyDetailLoading,
  selectCompanySaving,
  selectCompanyDeleting,
} from '../../../store/slices/companiesSlice';
import AppMultiSelect from '../../../components/common/AppMultiSelect';
import { COUNTRIES, getStatesForCountry } from '../../../utils/countryStates';
import { getUserDisplayName } from '../../../utils/userDisplay';
import {
  isSessionActive,
  formatSessionRemaining,
} from '../../../utils/accessSession';
import {
  ACCESS_DURATION_OPTIONS,
  DEFAULT_ACCESS_DURATION_MINUTES,
  formatAccessDurationLabel,
} from '../../../constants/accessDuration';

const COMPANIES_PAGE_SIZE = 10;

const emptyForm = {
  companyName: '',
  country: 'USA',
  states: [],
};

const emptyAdmins = (country) => ({
  bridgeAdmins: [emptyPerson(getDialCodeForBridgeCountry(country))],
});

const parseStates = (state) => {
  if (!state) return [];
  if (Array.isArray(state)) return state.map(String).map((s) => s.trim()).filter(Boolean);
  return String(state)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

const adminsFromCompany = (company, country) => {
  const list = (company?.bridgeAdmins || company?.bridge?.admins || []).map(mapPersonFromApi);
  return list.length ? list : emptyAdmins(country).bridgeAdmins;
};

const SESSION_EXPIRED_BANNER_MS = 2500;

const buildCompanySearchText = (company, access) => {
  const adminFields = (company.bridgeAdmins || company.bridge?.admins || [])
    .flatMap((admin) => [admin?.name, admin?.email, admin?.phone])
    .filter(Boolean);

  return [
    company.companyName,
    company.country,
    company.state,
    company.bridgeName,
    getRespondedAdminName(access),
    ...adminFields,
    String(company.bridgeAdmins?.length ?? company.bridge?.admins?.length ?? 0),
  ]
    .join(' ')
    .toLowerCase();
};

const matchesCompanySearch = (company, access, query) => {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const haystack = buildCompanySearchText(company, access);
  return terms.every((term) => haystack.includes(term));
};

export default function CompaniesPage() {
  const dispatch = useDispatch();
  const { showToast } = useNotify();

  const companies = useSelector(selectCompanies);
  const listLoading = useSelector(selectCompaniesLoading);
  const detail = useSelector(selectCompanyDetail);
  const detailLoading = useSelector(selectCompanyDetailLoading);
  const saving = useSelector(selectCompanySaving);
  const deleting = useSelector(selectCompanyDeleting);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [bridgeAdmins, setBridgeAdmins] = useState(() => emptyAdmins('USA').bridgeAdmins);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pendingCompany, setPendingCompany] = useState(null);
  const [expandedCompanyId, setExpandedCompanyId] = useState(null);
  const [companyAccessMap, setCompanyAccessMap] = useState({});
  const [now, setNow] = useState(() => Date.now());
  const pollRef = useRef(null);
  const prevSessionActiveRef = useRef({});
  const manualSessionCloseRef = useRef(new Set());
  const sessionExpiredBannerTimersRef = useRef({});
  const [closingSessionId, setClosingSessionId] = useState(null);
  const [cancellingRequestId, setCancellingRequestId] = useState(null);
  const [sessionExpiredBannerUntil, setSessionExpiredBannerUntil] = useState({});
  const [durationModalCompany, setDurationModalCompany] = useState(null);
  const [selectedDurationMinutes, setSelectedDurationMinutes] = useState(DEFAULT_ACCESS_DURATION_MINUTES);
  const [submittingAccessRequest, setSubmittingAccessRequest] = useState(false);

  useEffect(() => {
    dispatch(fetchCompanies());
  }, [dispatch]);

  useEffect(() => {
    if (!menuOpenId) return undefined;
    const onDocClick = () => setMenuOpenId(null);
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpenId(null);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpenId]);

  useEffect(() => {
    const nextMap = {};
    for (const company of companies) {
      if (company.accessRequest) {
        nextMap[company._id] = company.accessRequest;
      }
    }
    setCompanyAccessMap((prev) => ({ ...prev, ...nextMap }));
  }, [companies]);

  const updateCompanyAccess = useCallback((companyId, accessRequest) => {
    if (!companyId || !accessRequest) return;
    setCompanyAccessMap((prev) => ({
      ...prev,
      [companyId]: accessRequest,
    }));
  }, []);

  const clearPendingState = useCallback(() => {
    setPendingCompany(null);
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const handleAccessResolved = useCallback((accessRequest) => {
    const companyId = accessRequest?.company?._id || accessRequest?.company;
    if (!companyId) return;
    updateCompanyAccess(companyId, accessRequest);
    if (accessRequest.status !== 'pending') {
      clearPendingState();
      if (accessRequest.status === 'approved' && isSessionActive(accessRequest)) {
        showToast('Bridge admin approved your access request.');
        setExpandedCompanyId(String(companyId));
      } else if (accessRequest.status === 'rejected') {
        showToast('Bridge admin rejected your access request.', 'error');
      }
    }
  }, [clearPendingState, showToast, updateCompanyAccess]);

  const startPollingRequest = useCallback((requestId, company) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const data = await accessRequestService.getById(requestId);
        if (data.accessRequest) {
          handleAccessResolved(data.accessRequest);
        }
      } catch {
        // Keep polling while pending overlay is open.
      }
    }, 3000);
    setPendingCompany(company);
  }, [handleAccessResolved]);

  useEffect(() => {
    const onSocket = (event) => {
      const detail = event?.detail;
      if (detail?.type !== 'access-request:updated' || !detail.accessRequest) return;
      handleAccessResolved(detail.accessRequest);
    };
    window.addEventListener('shm:socket-event', onSocket);
    return () => window.removeEventListener('shm:socket-event', onSocket);
  }, [handleAccessResolved]);

  const showSessionExpiredBanner = useCallback((companyId) => {
    const key = String(companyId);
    const until = Date.now() + SESSION_EXPIRED_BANNER_MS;
    setSessionExpiredBannerUntil((prev) => ({ ...prev, [key]: until }));

    if (sessionExpiredBannerTimersRef.current[key]) {
      clearTimeout(sessionExpiredBannerTimersRef.current[key]);
    }
    sessionExpiredBannerTimersRef.current[key] = setTimeout(() => {
      setSessionExpiredBannerUntil((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      delete sessionExpiredBannerTimersRef.current[key];
    }, SESSION_EXPIRED_BANNER_MS);
  }, []);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    Object.values(sessionExpiredBannerTimersRef.current).forEach(clearTimeout);
    sessionExpiredBannerTimersRef.current = {};
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!expandedCompanyId) return;
    const access = companyAccessMap[expandedCompanyId];
    if (access && !isSessionActive(access, now)) {
      setExpandedCompanyId(null);
    }
  }, [now, expandedCompanyId, companyAccessMap]);

  useEffect(() => {
    Object.entries(companyAccessMap).forEach(([companyId, access]) => {
      const active = isSessionActive(access, now);
      const wasActive = prevSessionActiveRef.current[companyId];
      if (wasActive && !active && access?.status === 'approved') {
        if (manualSessionCloseRef.current.has(companyId)) {
          manualSessionCloseRef.current.delete(companyId);
        } else {
          showToast('Session timed out. Request access again.', 'error');
          showSessionExpiredBanner(companyId);
        }
      }
      prevSessionActiveRef.current[companyId] = active;
    });
  }, [now, companyAccessMap, showToast, showSessionExpiredBanner]);

  const handleCloseSession = async (event, company, access) => {
    event.preventDefault();
    event.stopPropagation();
    if (!access?._id || closingSessionId) return;

    setClosingSessionId(access._id);
    try {
      const data = await accessRequestService.closeSession(company._id, access._id);
      if (!data.accessRequest) {
        showToast('Failed to close session.', 'error');
        return;
      }
      manualSessionCloseRef.current.add(String(company._id));
      updateCompanyAccess(company._id, data.accessRequest);
      setExpandedCompanyId((prev) => (String(prev) === String(company._id) ? null : prev));
      showToast('Session closed.');
    } catch (e) {
      showToast(e.message || 'Failed to close session.', 'error');
    } finally {
      setClosingSessionId(null);
    }
  };

  const handleCancelRequest = async (event, company, access) => {
    event.preventDefault();
    event.stopPropagation();
    if (!access?._id || cancellingRequestId) return;

    setCancellingRequestId(access._id);
    try {
      const data = await accessRequestService.cancel(access._id);
      // Overwrite (not delete) the map entry so the stale pending request
      // from the companies list doesn't resurface via the fallback lookup.
      updateCompanyAccess(company._id, data?.accessRequest || { ...access, status: 'cancelled' });
      if (pendingCompany && String(pendingCompany._id) === String(company._id)) {
        clearPendingState();
      }
      showToast('Access request cancelled.');
    } catch (e) {
      showToast(e.message || 'Failed to cancel request.', 'error');
    } finally {
      setCancellingRequestId(null);
    }
  };

  const handleCompanyNameClick = async (company) => {
    const existing = companyAccessMap[company._id] || company.accessRequest;
    if (existing?.status === 'pending') {
      const requestId = existing._id;
      startPollingRequest(requestId, company);
      return;
    }
    if (isSessionActive(existing, now)) {
      setExpandedCompanyId((prev) => (String(prev) === String(company._id) ? null : company._id));
      return;
    }

    setSelectedDurationMinutes(DEFAULT_ACCESS_DURATION_MINUTES);
    setDurationModalCompany(company);
  };

  const closeDurationModal = () => {
    if (submittingAccessRequest) return;
    setDurationModalCompany(null);
  };

  const submitAccessRequest = async () => {
    if (!durationModalCompany || submittingAccessRequest) return;

    setSubmittingAccessRequest(true);
    const company = durationModalCompany;
    try {
      const data = await accessRequestService.createForCompany(
        company._id,
        selectedDurationMinutes
      );
      const accessRequest = data.accessRequest;
      if (!accessRequest) {
        showToast('Failed to create access request.', 'error');
        return;
      }
      updateCompanyAccess(company._id, accessRequest);
      setDurationModalCompany(null);
      if (accessRequest.status === 'pending') {
        startPollingRequest(accessRequest._id, company);
        if (data.created === false) {
          showToast('Approval is already pending for this company.');
        }
      } else {
        handleAccessResolved(accessRequest);
      }
    } catch (e) {
      showToast(e.message || 'Failed to request access.', 'error');
    } finally {
      setSubmittingAccessRequest(false);
    }
  };

  const getCompanyAccess = (company) => companyAccessMap[company._id] || company.accessRequest || null;

  const filtered = useMemo(
    () => companies.filter((company) => {
      const access = companyAccessMap[company._id] || company.accessRequest || null;
      return matchesCompanySearch(company, access, search);
    }),
    [companies, search, companyAccessMap]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / COMPANIES_PAGE_SIZE) || 1);
  const currentPage = Math.min(page, totalPages);
  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * COMPANIES_PAGE_SIZE;
    return filtered.slice(start, start + COMPANIES_PAGE_SIZE);
  }, [filtered, currentPage]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    if (!expandedCompanyId || !search.trim()) return;
    const stillVisible = filtered.some((company) => String(company._id) === String(expandedCompanyId));
    if (!stillVisible) setExpandedCompanyId(null);
  }, [search, filtered, expandedCompanyId]);

  const closeAdd = () => {
    setAddOpen(false);
    setForm(emptyForm);
    setBridgeAdmins(emptyAdmins('USA').bridgeAdmins);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditingId(null);
    setForm(emptyForm);
    setBridgeAdmins(emptyAdmins('USA').bridgeAdmins);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    dispatch(clearCompanyDetail());
  };

  const openAdd = () => {
    setMenuOpenId(null);
    setEditOpen(false);
    setEditingId(null);
    setForm(emptyForm);
    setBridgeAdmins(emptyAdmins(emptyForm.country).bridgeAdmins);
    setAddOpen(true);
  };

  const openDetail = (id) => {
    setMenuOpenId(null);
    setDetailOpen(true);
    dispatch(fetchCompanyById(id));
  };

  const openEdit = (company) => {
    setMenuOpenId(null);
    setAddOpen(false);
    const country = company.country || emptyForm.country;
    setForm({
      companyName: company.companyName || '',
      country,
      states: parseStates(company.state),
    });
    setBridgeAdmins(adminsFromCompany(company, country));
    setEditingId(company._id);
    setEditOpen(true);
  };

  const openDelete = (company) => {
    setMenuOpenId(null);
    setDeleteTarget({ id: company._id, name: company.companyName });
  };

  const onCountryChange = (country) => {
    setBridgeAdmins((prev) => applyCompanyCountryToAdmins(prev, form.country, country));
    setForm((p) => ({ ...p, country, states: [] }));
  };

  const stateOptions = getStatesForCountry(form.country);

  const buildCompanyPayload = () => ({
    companyName: form.companyName.trim(),
    country: form.country,
    state: form.states.join(', '),
    bridgeAdmins: filterCompanyBridgeAdmins(bridgeAdmins),
  });

  const validateCompanyForm = () => {
    if (!form.companyName.trim()) {
      showToast('Company name is required.', 'error');
      return false;
    }
    if (!form.states.length) {
      showToast('State is required.', 'error');
      return false;
    }
    const err = validateCompanyBridgeAdmins(bridgeAdmins);
    if (err) {
      showToast(err, 'error');
      return false;
    }
    return true;
  };

  const handleCreate = async () => {
    if (!validateCompanyForm()) return;

    try {
      await dispatch(createCompany(buildCompanyPayload())).unwrap();
      showToast('Company created successfully!');
      closeAdd();
      dispatch(fetchCompanies());
    } catch (e) {
      showToast(e || 'Failed to create company', 'error');
    }
  };

  const handleUpdate = async () => {
    if (!editingId || !validateCompanyForm()) return;

    try {
      await dispatch(updateCompany({ id: editingId, payload: buildCompanyPayload() })).unwrap();
      showToast('Company updated successfully!');
      closeEdit();
      dispatch(fetchCompanies());
    } catch (e) {
      showToast(e || 'Failed to update company', 'error');
    }
  };

  const confirmDeleteCompany = async () => {
    if (!deleteTarget?.id) return;
    try {
      await dispatch(deleteCompany(deleteTarget.id)).unwrap();
      showToast('Company and its bridges deleted successfully.');
      if (String(expandedCompanyId) === String(deleteTarget.id)) {
        setExpandedCompanyId(null);
      }
      if (pendingCompany && String(pendingCompany._id) === String(deleteTarget.id)) {
        clearPendingState();
      }
      if (detailOpen && detail && String(detail._id) === String(deleteTarget.id)) {
        closeDetail();
      }
      setDeleteTarget(null);
      dispatch(fetchCompanies());
    } catch (e) {
      showToast(e || 'Failed to delete company', 'error');
    }
  };

  const adminCount = (c) => (c.bridgeAdmins?.length ?? c.bridge?.admins?.length ?? 0);
  const creatorName = detail ? getUserDisplayName(detail.createdBy) : null;
  const pendingAccess = pendingCompany
    ? getCompanyAccess(pendingCompany)
    : null;
  const pendingDurationLabel = formatAccessDurationLabel(pendingAccess?.requestedDurationMinutes);

  return (
    <div className="companies-page">
      <button type="button" className="companies-add-btn" onClick={openAdd}>
        <i className="ti ti-plus" /> Add a new company
      </button>

      <div className="companies-table-card">
        <div className="companies-table-header">
          <div className="companies-table-title">
            <i className="ti ti-list" />
            Listed companies
          </div>
          <div className="companies-search">
            <i className="ti ti-search" />
            <input
              type="text"
              placeholder="Search companies..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        {listLoading ? (
          <Loading message="Loading companies..." />
        ) : (
          <table className="companies-table">
            <thead>
              <tr>
                <th className="col-index">Sr.no.</th>
                <th className="col-company">Company</th>
                <th className="col-location">Location</th>
                <th className="col-admins">Admins</th>
                <th className="col-details">Details</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="5" className="companies-empty">
                    No companies found.
                  </td>
                </tr>
              ) : (
                pageItems.map((c, idx) => {
                  const access = getCompanyAccess(c);
                  const sessionActive = isSessionActive(access, now);
                  const sessionExpiredBannerVisible = Boolean(
                    sessionExpiredBannerUntil[String(c._id)] && now < sessionExpiredBannerUntil[String(c._id)]
                  );
                  const isExpanded = String(expandedCompanyId) === String(c._id) && sessionActive;
                  const admins = adminCount(c);
                  return (
                  <React.Fragment key={c._id}>
                  <tr>
                    <td className="col-index">
                      {String((currentPage - 1) * COMPANIES_PAGE_SIZE + idx + 1).padStart(2, '0')}
                    </td>
                    <td className="col-company">
                      <div className="company-name-cell">
                        <div className="company-row-identity">
                          <div className="company-name-cell__main">
                            <button
                              type="button"
                              className="company-name-btn"
                              onClick={() => handleCompanyNameClick(c)}
                              title={
                                sessionActive
                                  ? 'Show bridges under approved admin'
                                  : 'Request access to bridge data'
                              }
                            >
                              <b>{c.companyName}</b>
                              {sessionActive && (
                                <i
                                  className={`ti ti-chevron-${isExpanded ? 'up' : 'down'}`}
                                  style={{ marginLeft: 6, fontSize: 12 }}
                                />
                              )}
                            </button>
                            {access?.status === 'pending' && (
                              <div className="company-access-status pending company-access-status-row">
                                <span>Approval pending...</span>
                                <button
                                  type="button"
                                  className="access-request-cancel"
                                  onClick={(e) => handleCancelRequest(e, c, access)}
                                  disabled={cancellingRequestId === access._id}
                                  title="Cancel request"
                                  aria-label="Cancel request"
                                >
                                  <i className="ti ti-x" />
                                </button>
                              </div>
                            )}
                            {sessionActive && (
                              <div className="company-access-status approved">
                                Approved by: {getRespondedAdminName(access) || 'Bridge admin'}
                              </div>
                            )}
                            {sessionExpiredBannerVisible && (
                              <div className="company-access-status session-expired">Session timed out</div>
                            )}
                            {access?.status === 'rejected' && (
                              <div className="company-access-status rejected">Request rejected</div>
                            )}
                          </div>
                        </div>
                        {sessionActive && (
                          <div className="session-timer" title="Access session time remaining">
                            <button
                              type="button"
                              className="session-timer-close"
                              onClick={(e) => handleCloseSession(e, c, access)}
                              disabled={closingSessionId === access._id}
                              title="Close session"
                              aria-label="Close session"
                            >
                              <i className="ti ti-x" />
                            </button>
                            <i className="ti ti-clock" />
                            <span>{formatSessionRemaining(access, now)}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="col-location">
                      <span className="company-location-country">{c.country}</span>
                      {c.state && (
                        <>
                          <span className="company-location-sep"> · </span>
                          <span className="company-location-state">{c.state}</span>
                        </>
                      )}
                    </td>
                    <td className="col-admins">
                      <span className={`admin-badge ${admins > 0 ? 'has-admin' : 'no-admin'}`}>
                        <span className="admin-badge-dot" />
                        {admins} admin{admins !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td className="col-details">
                      <div
                        className={`company-actions-menu${menuOpenId === c._id ? ' is-open' : ''}`}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="company-actions-trigger"
                          aria-label={`Actions for ${c.companyName}`}
                          aria-expanded={menuOpenId === c._id}
                          aria-haspopup="menu"
                          onClick={() =>
                            setMenuOpenId((prev) => (prev === c._id ? null : c._id))
                          }
                        >
                          <i className="ti ti-dots" />
                        </button>
                        {menuOpenId === c._id && (
                          <div className="company-actions-dropdown" role="menu">
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => openDetail(c._id)}
                            >
                              <i className="ti ti-eye" /> View
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => openEdit(c)}
                            >
                              <i className="ti ti-pencil" /> Edit
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              className="is-danger"
                              onClick={() => openDelete(c)}
                            >
                              <i className="ti ti-trash" /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="company-row-expanded">
                      <td colSpan="5">
                        <CompanyAdminBridgePanel company={c} accessRequest={access} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        )}
        {!listLoading && filtered.length > COMPANIES_PAGE_SIZE && (
          <div className="companies-pagination">
            <span>
              Showing {(currentPage - 1) * COMPANIES_PAGE_SIZE + 1}
              –
              {Math.min(currentPage * COMPANIES_PAGE_SIZE, filtered.length)}
              {' '}of {filtered.length}
            </span>
            <div className="companies-pagination-actions">
              <button
                type="button"
                className="companies-pagination-btn"
                disabled={currentPage <= 1}
                onClick={() => {
                  setMenuOpenId(null);
                  setPage(currentPage - 1);
                }}
              >
                Previous
              </button>
              <span className="companies-pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                className="companies-pagination-btn"
                disabled={currentPage >= totalPages}
                onClick={() => {
                  setMenuOpenId(null);
                  setPage(currentPage + 1);
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {pendingCompany && (
        <div className="pending-overlay">
          <div className="pending-overlay-card">
            <LoaderSpinner size={32} />
            <h3>Approval pending</h3>
            <p>
              Waiting for a bridge admin to approve access to <strong>{pendingCompany.companyName}</strong> bridge data
              {pendingDurationLabel ? (
                <> for <strong>{pendingDurationLabel}</strong></>
              ) : null}
              .
            </p>
          </div>
        </div>
      )}

      {durationModalCompany && (
        <Modal title="Request bridge data access" onClose={closeDurationModal} width={480}>
          <p style={{ marginBottom: 16, color: '#555', fontSize: 14 }}>
            Select how long you need access to <strong>{durationModalCompany.companyName}</strong> bridge data.
            The bridge admin will see this duration before approving.
          </p>
          <div className="form-group">
            <label>Access duration</label>
            <select
              value={selectedDurationMinutes}
              onChange={(e) => setSelectedDurationMinutes(Number(e.target.value))}
              disabled={submittingAccessRequest}
            >
              {ACCESS_DURATION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <LoadingButton
              className="btn btn-teal"
              style={{ flex: 1, padding: 10 }}
              onClick={submitAccessRequest}
              loading={submittingAccessRequest}
              loadingLabel="Sending..."
            >
              Send approval request
            </LoadingButton>
            <button
              type="button"
              className="btn"
              style={{ padding: '10px 16px' }}
              onClick={closeDurationModal}
              disabled={submittingAccessRequest}
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {(addOpen || editOpen) && (
        <Modal
          title={editOpen ? 'Edit Company' : 'Add New Company'}
          onClose={editOpen ? closeEdit : closeAdd}
          width={820}
          className="modal--add-company"
        >
          <div className="form-row">
            <div className="form-group">
              <label>Select Country</label>
              <select value={form.country} onChange={(e) => onCountryChange(e.target.value)}>
                {COUNTRIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Select State</label>
              <AppMultiSelect
                values={form.states}
                onChange={(states) => setForm((p) => ({ ...p, states }))}
                options={stateOptions.map((s) => ({ value: s, label: s }))}
                placeholder="Select State"
                checkboxPosition="right"
                aria-label="Select State"
              />
            </div>
            <div className="form-group">
              <label>Company Name</label>
              <input
                type="text"
                value={form.companyName}
                placeholder="Enter company name"
                onChange={(e) => setForm((p) => ({ ...p, companyName: e.target.value }))}
              />
            </div>
          </div>

          <UnlimitedAdminList
            title="Admin"
            list={bridgeAdmins}
            bridgeCountry={form.country}
            onChange={setBridgeAdmins}
          />

          <LoadingButton
            className="btn btn-teal"
            style={{ width: '100%', padding: 10, fontSize: 14, marginTop: 8 }}
            onClick={editOpen ? handleUpdate : handleCreate}
            loading={saving}
            loadingLabel="Saving..."
          >
            {editOpen ? 'Save changes' : 'Save company'}
          </LoadingButton>
        </Modal>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete Company"
          message={
            <>
              Delete <strong>{deleteTarget.name}</strong>? This will also remove all bridges under
              this company for bridge admins, managers, engineers, and owners. This cannot be undone.
            </>
          }
          confirmLabel="Delete"
          variant="danger"
          loading={deleting}
          onConfirm={confirmDeleteCompany}
          onCancel={() => !deleting && setDeleteTarget(null)}
        />
      )}

      {detailOpen && (
        <Modal title="Company Details" onClose={closeDetail} width={720}>
          {detailLoading ? (
            <Loading message="Loading company..." />
          ) : !detail ? (
            <p style={{ color: '#888' }}>Company not found.</p>
          ) : (
            <>
              <div className="bridge-step-summary" style={{ marginBottom: 16 }}>
                <div className="bridge-step-summary-text">
                  <strong>{detail.companyName}</strong>
                  <span>
                    {detail.state}, {detail.country}
                  </span>
                </div>
              </div>

              <div style={{ fontWeight: 700, fontSize: 13, color: '#1a6fa8', marginBottom: 8 }}>
                Admins
              </div>
              <table style={{ marginBottom: 14 }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.bridgeAdmins || detail.bridge?.admins || []).map((a, i) => (
                    <tr key={a._id || i}>
                      <td>{a.name || a.user?.name}</td>
                      <td>{a.email || a.user?.email}</td>
                      <td>
                        {a.countryCode ? `+${String(a.countryCode).replace(/^\+/, '')} ` : ''}
                        {a.phone || '—'}
                      </td>
                    </tr>
                  ))}
                  {(detail.bridgeAdmins || detail.bridge?.admins || []).length === 0 && (
                    <tr>
                      <td colSpan="3" style={{ textAlign: 'center', color: '#aaa' }}>
                        No admins
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {creatorName && (
                <p style={{ fontSize: 11, color: '#888' }}>
                  Created by {creatorName}
                  {detail.createdAt &&
                    ` - ${new Date(detail.createdAt).toLocaleString()}`}
                </p>
              )}
            </>
          )}
        </Modal>
      )}
    </div>
  );
}
